import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import {
    Layout,
    Button,
    Input,
    Typography,
    Row,
    Col,
    Spin,
    Table,
    Space,
    Tag,
    Popover,
    Image
} from 'antd';
import { PlusCircleOutlined, CheckCircleOutlined, StarOutlined, StarTwoTone} from '@ant-design/icons';
import EditItem from '../AddMenu/EditItem';
import AddNewItem from '../AddMenu/AddNewItem';
import { useQuery, useMutation, NetworkStatus } from '@apollo/client';
import { GET_PAGINATED_ITEMS, GET_FAVORITES, GET_USER_TAGS, GET_ITEM_TAGS } from '../../queries';
import { HIDE_ITEM, DESTROY_ITEMS, CREATE_FAVORITE, DELETE_FAVORITE, CREATE_ITEM_TAG, DELETE_ITEM_TAG } from '../../mutations';
import moment from 'moment';
import { withRouter, useHistory, useParams } from 'react-router-dom';
import { getDefaultDepartment } from '../../Utilities/departmentUtils';

import { Tags } from '../Estimations/Estimations';

// IME-aware debounce hook for Japanese input
// Only updates the debounced value after IME composition is complete
const useIMEAwareDebounce = (value, delay, isComposing) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    
    useEffect(() => {
        // Don't update while user is composing (typing Japanese with IME)
        if (isComposing) {
            return;
        }
        
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay, isComposing]);
    
    return debouncedValue;
};

const ItemList = ({ shouldRefetch, setShouldRefetch, department: propDepartment }) => {
    const history = useHistory();
    const { department: routeDepartment } = useParams();
    const activeDepartment = propDepartment || routeDepartment || getDefaultDepartment();
    const [addingItem, setAddingItem] = useState(false);
    const [editingItem, setEditingItem] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [itemNameId, setItemNameId] = useState(null);
    
    const [rows, setRows] = useState([]);
    const [imageSrc, setImageSrc] = useState();
    const [zoom, setZoom] = useState();
    const [currentPage, setCurrentPage] = useState(parseInt(localStorage.getItem('prodManagementPage')) || 1);
    const [pageSize, setPageSize] = useState(parseInt(localStorage.getItem('prodManagementPageSize')) || 5);
    const [filterFavorites, setFilterFavorites] = useState(false);
    const [filterCurrentMonth, setFilterCurrentMonth] = useState(false);
    const [filterHasParentProdId, setFilterHasParentProdId] = useState(false);
    const [showRegularItems, setShowRegularItems] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedTags, setSelectedTags] = useState([]);
    
    // Track IME composition state for Japanese input
    const [isComposing, setIsComposing] = useState(false);
    
    // IME-aware debounce - waits for composition to complete before searching
    const debouncedSearchText = useIMEAwareDebounce(searchText, 400, isComposing);

    const currentUser = useMemo(() => {
        try {
            const stored = localStorage.getItem('currentUser');
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    }, [])

    // When search/filter context changes, Apollo must not run with the previous page (useEffect runs too late).
    // queryPage is derived synchronously; useLayoutEffect aligns currentPage + localStorage before paint.
    const selectedTagsKey = useMemo(
        () => [...selectedTags].sort((a, b) => a - b).join(','),
        [selectedTags]
    );
    const paginationFilterKey = useMemo(
        () =>
            JSON.stringify({
                search: debouncedSearchText,
                favorites: filterFavorites,
                tagsKey: selectedTagsKey,
                department: activeDepartment,
                hasParentProdId: filterHasParentProdId,
                currentMonth: filterCurrentMonth,
                showRegularItems,
            }),
        [
            debouncedSearchText,
            filterFavorites,
            selectedTagsKey,
            activeDepartment,
            filterHasParentProdId,
            filterCurrentMonth,
            showRegularItems,
        ]
    );
    const prevPaginationFilterKeyRef = useRef(null);
    const queryPage = useMemo(() => {
        const prev = prevPaginationFilterKeyRef.current;
        if (prev !== null && prev !== paginationFilterKey) {
            return 1;
        }
        return currentPage;
    }, [paginationFilterKey, currentPage]);

    useLayoutEffect(() => {
        const prev = prevPaginationFilterKeyRef.current;
        if (prev !== null && prev !== paginationFilterKey && currentPage !== 1) {
            setCurrentPage(1);
            localStorage.setItem('prodManagementPage', 1);
        }
        prevPaginationFilterKeyRef.current = paginationFilterKey;
    }, [paginationFilterKey, currentPage]);

    const { data: rawData, loading, refetch, networkStatus } = useQuery(GET_PAGINATED_ITEMS, { 
        errorPolicy: 'all', 
        notifyOnNetworkStatusChange: true,
        variables: { 
            department: activeDepartment,
            page: queryPage,
            perPage: pageSize,
            search: debouncedSearchText || null,
            favoritesOnly: filterFavorites,
            currentMonth: filterCurrentMonth,
            showRegularItems: showRegularItems,
            tagIds: selectedTags.length > 0 ? selectedTags : null,
            hasParentProdId: filterHasParentProdId
        },
        fetchPolicy: 'network-only'  // Always fetch fresh data for pagination
    });

    const { data: favorites, refetch: refetchFavorites } = useQuery(GET_FAVORITES);

    const { data: tags, refetch: refetchTags } = useQuery(GET_USER_TAGS, { variables: { department: activeDepartment }, fetchPolicy: 'no-cache' });

    const {data: itemTags, refetch: refetchItemTags} = useQuery(GET_ITEM_TAGS, {variables: { department: activeDepartment }, fetchPolicy: 'no-cache'});

    useEffect(() => {
        if (shouldRefetch !== 'tags') {
            return;
        }
        // Reset after refetch so a second `setShouldRefetch('tags')` toggles false → 'tags' and this effect runs again.
        Promise.all([refetchTags(), refetchItemTags()])
            .catch((error) => {
                console.error('Failed to refetch tags or item tags:', error);
            })
            .finally(() => {
                if (setShouldRefetch) {
                    setShouldRefetch(false);
                }
            });
    }, [shouldRefetch, refetchTags, refetchItemTags, setShouldRefetch]);

    // Switching 事業部 (e.g. sweets → snacks): tag ids and filters are department-specific.
    // Leaving them on causes paginatedItems to request snack rows with sweets-only tag / favorite filters → empty list.
    useEffect(() => {
        setSelectedTags([]);
        setFilterFavorites(false);
        setFilterCurrentMonth(false);
        setFilterHasParentProdId(false);
    }, [activeDepartment]);

    const formattedTags = useMemo(() => {
        if (tags) {
            return tags.tags.map(tag => {
                return {
                    label: tag.name,
                    value: tag.color,
                    id: tag.id
                }
            })
        }
        return []
    }, [tags])
    
    // Parse paginated data from server
    const parsedData = useMemo(() => {
        if (rawData?.paginatedItems) {
            try {
                return JSON.parse(rawData.paginatedItems);
            } catch (e) {
                console.error('Failed to parse paginated items:', e);
                return null;
            }
        }
        return null;
    }, [rawData]);

    const [createFavorite] = useMutation(CREATE_FAVORITE, {
        onCompleted: () => {
            refetchFavorites();
        },
        onError: (error) => {
            console.log(error);
        }
    })

    const [deleteFavorite] = useMutation(DELETE_FAVORITE, {
        onCompleted: () => {
            refetchFavorites();
        },
        onError: (error) => {
            console.log(error);
        }
    })

    const [tagItem] = useMutation(CREATE_ITEM_TAG, {
        onCompleted: () => {
            refetchItemTags().catch((error) => {
                console.error('Refetch itemTags failed', error);
            });
        },
        onError: (error) => {
            console.log(error);
        }
    })

    const [untagItem] = useMutation(DELETE_ITEM_TAG, {
        onCompleted: () => {
            refetchItemTags().catch((error) => {
                console.error('Refetch itemTags failed', error);
            });
        },
        onError: (error) => {
            console.log(error);
        }
    })

    const [hideItem] = useMutation(HIDE_ITEM, {
        onCompleted: (data) => {
            refetch();
            localStorage.setItem('itemsRefetchFlag', 1);
        },
        onError: (error) => {
            console.log(error);
        }
    });

    const [deleteItem] = useMutation(DESTROY_ITEMS, {
        onCompleted: (data) => {
            refetch();
            localStorage.setItem('itemsRefetchFlag', 1);
        },
        onError: (error) => {
            console.log(error);
        }
    });

    // Process paginated data from server
    useEffect(() => {
        if (parsedData) {
            const items = parsedData.items || [];
            const formattedRows = items.map(itemName => {
                const latestCycle = itemName.item_selling_cycles && itemName.item_selling_cycles.length > 0 ? 
                    itemName.item_selling_cycles.reduce((prev, curr) => curr.selling_end_dt > prev?.selling_end_dt ? curr : prev) : null
                const profile = itemName.item_profile || null
                const status = itemName.user_defined_status ? itemName.user_defined_status : itemName.status;
                const isFavorite = favorites?.favorites?.map(favorite => parseInt(favorite.itemNameId)).includes(parseInt(itemName.id));
                const currentItemTags = itemTags?.itemTags.filter(itemTag => parseInt(itemTag.itemName.id) === itemName.id)
                const formattedItemTags = currentItemTags?.map(t => ({id: t.id, label: t.tag.name, value: t.tag.color, tagId: t.tag.id}))
                return {
                    key: itemName.id,
                    id: itemName.id,
                    name: itemName.item_name,
                    nameInfo: { itemNameId: itemName.id, name: itemName.item_name },
                    description: profile?.item_detail,
                    image: { url: profile?.download_url || "no url", name: profile?.item_img_file || "image.png"},
                    products: itemName.item_mappings,
                    hidden: itemName.hidden,
                    status,
                    size: itemName.item_size || '',
                    updated_at: itemName.updated_at,
                    updated_by: itemName.updated_by_user?.email || '',
                    selling_period: itemName.item_selling_cycles && itemName.item_selling_cycles.length > 0 ? {
                        period: `${latestCycle.selling_start_dt.split('T')[0]} - ${latestCycle.selling_end_dt.split('T')[0]}`,
                        start: latestCycle.selling_start_dt.split('T')[0],
                        end: latestCycle.selling_end_dt.split('T')[0],
                    } : null,
                    latestCycle: latestCycle,
                    latest_price: latestCycle?.regular_price,
                    favorite: isFavorite,
                    tags: formattedItemTags,
                    productCodes: itemName.item_mappings?.map(e => e.prod_id),
                }
            });
            setRows(formattedRows);
            setTotalCount(parsedData.pagination?.total_count || 0);
        }
    }, [parsedData, favorites, itemTags]);

    useEffect(() => {
        if (history?.location?.state?.searchText) {
            setSearchText(history.location.state.searchText)
            delete history.location.state.searchText
            history.replace(history.location)
        }
    }, [history])
    const columns = [
        {
            title: <Typography.Text strong></Typography.Text>,
            dataIndex: 'favorite',
            width: 50,
            render: (favorite, row) => {
                return !favorite ? <StarOutlined style={{fontSize: "20px", color: "lightgrey"}} onClick={e => createFavorite({ variables: { input: { itemNameId: row.key}}})} />
                : <StarTwoTone style={{fontSize: "20px"}} onClick={e => deleteFavorite({ variables: { input: { itemNameId: row.key }}})} />
            }
        },
        {
            title: <Typography.Text strong>タグ</Typography.Text>,
            dataIndex: 'tags',
            width: 100,
            render: (tags, row) => {
                return <Tags currentTags={tags} tagList={formattedTags} tagItem={tagItem} untagItem={untagItem} itemNameId={row.key}/>
            }
        },
        {
            title: <Typography.Text strong>商品名</Typography.Text>,
            dataIndex: 'nameInfo',
            width: 180,
            render: x => <a style={{ color: 'black' }} onClick={() => { // eslint-disable-line
                if (activeDepartment === 'sweets') {
                    history.push('/itemDetails/' + activeDepartment + '/' + x.itemNameId)
                } else {
                    history.push('/estimations/' + activeDepartment + '/' + x.itemNameId)
                }
            }}>{x.name}</a>,
        },

        {
            title: <Typography.Text strong>商品説明</Typography.Text>,
            dataIndex: 'description',
            width: 250,
            render: desc => {
                return desc?.length > 100 ? desc?.substr(0,97) + "..." : desc?.substr(0,100)
            }            
        },
        {
            title: <Typography.Text strong>商品ID</Typography.Text>,
            dataIndex: 'key',
            sorter: (a, b) => parseInt(a.key) - parseInt(b.key),
            width: 75
        },
        {
            title: <Typography.Text strong>製品</Typography.Text>,
            dataIndex: 'products',
            sorter: (a, b) => a.products ? true : false,
            render: products => {
                return products ? products.map((e, i) => (
                    <div key={e.prod_id + e.prod_name}>
                        {e.prod_id} - {e.prod_name}
                    </div>
                )) : null
            },
            width: 200
        },
        ...(activeDepartment === 'sweets' ? [{
            title: <Typography.Text strong>集約親管理コード</Typography.Text>,
            dataIndex: 'products',
            sorter: (a, b) => {
                const idsA = [...new Set((a.products || []).map(e => e.parent_prod_id).filter(v => v != null))].sort((x, y) => x - y);
                const idsB = [...new Set((b.products || []).map(e => e.parent_prod_id).filter(v => v != null))].sort((x, y) => x - y);
                const minA = idsA[0];
                const minB = idsB[0];
                if (minA == null && minB == null) return 0;
                if (minA == null) return 1;
                if (minB == null) return -1;
                return minA - minB;
            },
            render: products => {
                if (!products?.length) return null;
                const distinct = [...new Set(products.map(e => e.parent_prod_id).filter(v => v != null))];
                if (distinct.length === 0) return '—';
                return distinct.map((id, i) => <div key={i}>{id}</div>);
            },
            width: 140
        }] : []),
        {
            title: <Typography.Text strong>販売期間</Typography.Text>,
            dataIndex: 'selling_period',
            sorter: (a, b, sortOrder) => {
                if (a.selling_period?.end && b.selling_period?.end) {
                    if (a.selling_period.end === b.selling_period.end) {
                        if (a.selling_period.start === b.selling_period.start) {
                            return 0
                        } else {
                            return a.selling_period.start > b.selling_period.start ? 1 : -1
                        }
                    } else {
                        return a.selling_period.end > b.selling_period.end ? 1 : -1
                    }
                } else if (sortOrder === 'ascend' && a.selling_period?.end) {
                    return -1;
                } else if (sortOrder === 'ascend' && b.selling_period?.end) {
                    return 1
                } else if (sortOrder === 'descend' && a.selling_period?.end) {
                    return 1;
                } else if (sortOrder === 'descend' && b.selling_period?.end) {
                    return -1
                }
                 else {
                    return 0;
                }                
            },
            render: x => x?.period,
            width: 150,
            sortDirections: ['descend', 'ascend']
        },
        {
            title: <Typography.Text strong>価格（税込）</Typography.Text>,
            dataIndex: 'latest_price',
            sorter: (a, b) => {
                if (a.latest_price && b.latest_price) {
                    return a.latest_price - b.latest_price;
                } else if (a.latest_price) {
                    return 1;
                } else if (b.latest_price) {
                    return -1
                } else {
                    return 0;
                }
            },
            render: x => (x ? `¥${parseInt(x).toLocaleString('ja-JP')}` : null),
            width: 125
        },
        {
            title: <Typography.Text strong>サイズ</Typography.Text>,
            dataIndex: 'size',
            sorter: (a, b) => a.size.localeCompare(b.size),
            render: x => (x || 'バラ'),
            width: 100
        },
        {
            title: <Typography.Text strong>商品画像</Typography.Text>,
            dataIndex: 'image',
            sorter: (a, b) => a.size.localeCompare(b.size),
            render: x => (x.url !== 'no url' ? <a onClick={e => { // eslint-disable-line
                setImageSrc(x.url)
                setZoom(true)
            }}>{x.name}</a> : null),
            width: 100
        },
        {
            title: <Typography.Text strong>定番商品</Typography.Text>,
            dataIndex: 'status',
            sorter: (a, b) => ((a.status === b.status) ? 0 : a.status === "Regular" ? -1 : 1),
            render: x => (x === "Regular" ? <CheckCircleOutlined style={{fontSize: "1rem", marginLeft: "40%"}}/> : null),
            width: 100
        },
        {
            title: <Typography.Text strong>ステータス</Typography.Text>,
            dataIndex: 'hidden',
            sorter: (a, b) => ((a.hidden === b.hidden) ? 0 : a.hidden ? -1 : 1),
            render: x => (<Tag color={x ? 'error' : 'success'}>{x ? '非表示' : '表示中'}</Tag>),
            width: 100
        },
        {
            title: <Typography.Text strong>編集者</Typography.Text>,
            dataIndex: 'updated_by',
            sorter: (a, b) => a.updated_by.localeCompare(b.updated_by),
            width: 180
        },
        {
            title: <Typography.Text strong>追加・変更日時</Typography.Text>,
            dataIndex: 'updated_at',
            align: 'right',
            sorter: (a, b) => {
                if (a.updated_at && b.updated_at) {
                    return a.updated_at < b.updated_at ? 1 : -1
                }
                return -1
            },
            render: x => 
                x ? <div>
                        <div>{moment(x).format('YYYY-MM-DD')}</div>
                        <div>{moment(x).format('a h:mm')}</div>
                    </div> : null,
            width: 125
        },
        {
            title: '',
            fixed: 'right',
            render: (text, rowContent) => {
                return <div style={{display: 'flex', justifyContent: "space-evenly"}}>
                <Button type='link' style={{ paddingRight: '.5rem', paddingLeft: '.5rem' }} onClick={() => {
                    setEditingItem(true);
                    setItemNameId(rowContent.key);
                }}>編集</Button>
                { loading ? 
                    <Spin /> :
                    <Button type='link' style={{ paddingRight: '.5rem', paddingLeft: '.5rem' }} onClick={() => {
                        hideItem({ variables: { input: { id: rowContent.key } } });
                    }}>
                        { rowContent.hidden ? '表示' : '非表示' }
                    </Button>
                }
                {currentUser?.admin && <Button type='link' onClick={() => {
                    setRows(rows.filter(e => e.key !== rowContent.key))
                    deleteItem({ variables: { input: { ids: [rowContent.key] } } })
                }}>削除</Button>}
            </div>
            },  
            width: 125
        }
    ];

    const handleTagChange = (tag, checked) => {
		const nextSelectedTags = checked ? [...selectedTags, tag] : selectedTags.filter((t) => t !== tag);
		setSelectedTags(nextSelectedTags);
	};
    
    const helpContent = <>
        <b>＜サイズで検索する場合＞</b>
        <p>半角英字でスペースを空けずに「size:S/M/L」と入力してください。
            <br></br>入力例：size:S、size:M、size:L、size:バラ</p>　
</>;


    const handleSearch = e => {
        setSearchText(e.target.value);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        localStorage.setItem('prodManagementPage', page);
    };

    const handlePageSizeChange = (current, size) => {
        setPageSize(size);
        setCurrentPage(1);  // Reset to first page when changing page size
        localStorage.setItem('prodManagementPageSize', size);
        localStorage.setItem('prodManagementPage', 1);
    };

    const EstimationsTable = <Layout style={{ padding: 24, background: '#fff' }}>
        <Row gutter={[16, 8]}>
            <Col>
                <Input.Search 
                    autoFocus 
                    style={{ width: '480px' }} 
                    value={searchText} 
                    onChange={(e) => {
                        handleSearch(e);
                        // Reset composition state if input is cleared (e.g., via allowClear button)
                        if (e.target.value === '') {
                            setIsComposing(false);
                        }
                    }}
                    onCompositionStart={() => setIsComposing(true)}
                    onCompositionEnd={() => setIsComposing(false)}
                    onBlur={() => setIsComposing(false)}
                    allowClear 
                    placeholder="検索" 
                />
                <Button type='link'>
                    <Popover title='商品の検索方法' trigger='click' content={helpContent}>
                            商品の検索方法
                    </Popover>
                </Button>
            </Col>
            <Col style={{ flex: 1 }} align='left'>
                <Space direction='horizontal'>
                    <Button.Group>
                        <Button 
                            onClick={() => {
                            setFilterFavorites((prev => !prev))
                            }} 
                            type={filterFavorites ? 'primary' : 'default'} 
                            >
                            {filterFavorites ? '全て表示' : 'お気に入りのみ表示'}
                        </Button>
                        <Button 
                            onClick={() => {
                                setFilterCurrentMonth((prev => !prev))
                            }} 
                            type={filterCurrentMonth ? 'primary' : 'default'} 
                            >
                            {filterCurrentMonth ? '今月販売中のみ' : '今月販売中'}
                        </Button>
                        <Button 
                            onClick={() => {
                            setShowRegularItems((prev => !prev))
                            }} 
                            type={showRegularItems ? 'primary' : 'default'} 
                            >
                            {showRegularItems ? '定番商品を含む' : '定番商品を含まない'}
                        </Button>
                        {activeDepartment === 'sweets' && (
                            <Button
                                onClick={() => setFilterHasParentProdId(prev => !prev)}
                                type={filterHasParentProdId ? 'primary' : 'default'}
                            >
                                {filterHasParentProdId ? '全て表示' : '集約親管理コードあり'}
                            </Button>
                        )}
                    </Button.Group>
                    {activeDepartment === 'sweets' && (
                        <Button
                            onClick={() => {
                                history.push("/addMenu/" + activeDepartment);
                                setAddingItem(true);
                            }}
                            type='primary'
                            icon={<PlusCircleOutlined />}>
                            商品追加
                        </Button>
                    )}
                </Space>
            </Col>
        </Row>
        <Row gutter={[16, 8]} style={{ marginTop: 8 }} align="middle">
            <Col span={24}>
                <span style={{ marginRight: 8 }}>タグ:</span>
                <Space size={[0, 8]} wrap>
                    {formattedTags.map((tag) => (
                        <Tag.CheckableTag
                            style={{ backgroundColor: selectedTags.includes(tag.id) ? tag.value : 'white', border: !selectedTags.includes(tag.id) && "1px solid "+tag.value, color: selectedTags.includes(tag.id) ? 'white' : tag.value}}
                            key={tag.id}
                            checked={selectedTags.includes(tag.id)}
                            onChange={(checked) => handleTagChange(tag.id, checked)}
                        >
                            {tag.label}
                        </Tag.CheckableTag>
                    ))}
                </Space>
            </Col>
        </Row>
        <br/>
        { loading && networkStatus !== NetworkStatus.refetch ?
            <Spin /> :
            <Table
                columns={columns}
                dataSource={rows}
                scroll={{ x: 2500 }}
                loading={loading}
                locale={{ emptyText: '該当する商品が見つかりませんでした' }}
                pagination={{
                    pageSize: pageSize,
                    current: currentPage,
                    total: totalCount,
                    onChange: handlePageChange,
                    onShowSizeChange: handlePageSizeChange,
                    pageSizeOptions: [5, 10, 20, 50, 100],
                    showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}件`,
                    showSizeChanger: true
                }}
            />
        }
    </Layout>;

    const AddNewItemWithHistory = withRouter(AddNewItem);

    return (
        <>
            { zoom && <div style={{ display: "none" }}>
                <Image
                    width={200}
                    placeholder={<Spin />}
                    preview={{
                        visible: zoom,
                        src: imageSrc,
                        onVisibleChange: (value) => {
                            setImageSrc('')
                            setZoom(value);
                        },
                    }}
                />
            </div>}
            {addingItem ? (
                <AddNewItemWithHistory setAddingItem={setAddingItem} refetch={refetch}
                    handleCancel={() => {
                        setAddingItem(false);
                    }}
                    department={activeDepartment}
                />
            ) : editingItem ? (
                <EditItem
                    itemNameId={itemNameId}
                    handleFinishEdit={() => {
                        setEditingItem(false);
                        refetch();
                    }}
                    itemNames={rows}
                    department={activeDepartment}
                />
            ) : (
                EstimationsTable
            )}
        </>
    );
};

export default ItemList;
